import {
  AutocompleteValue,
  Autocomplete as MuiAutocomplete,
  AutocompleteProps as MuiAutocompleteProps,
  TextField
} from '@mui/material';
import React, { useEffect, useState } from 'react';

type AutocompleteProps<
  T,
  Multiple extends boolean,
  DisableClearable extends boolean,
  FreeSolo extends boolean
> = {
  label: string;
  uniqueField?: keyof T;
  optionField?: keyof T;
  getOptionLabel?: ((option: T) => string) | undefined;
  errorMessage?: string;
  displayError?: boolean;
  onChange: (
    value: AutocompleteValue<T, Multiple, DisableClearable, FreeSolo>
  ) => void;
} & Omit<
  MuiAutocompleteProps<T, Multiple, DisableClearable, FreeSolo>,
  'onChange' | 'renderInput'
>;

export function Autocomplete<
  T,
  Multiple extends boolean = false,
  DisableClearable extends boolean = false,
  FreeSolo extends boolean = false
>({
  id,
  options,
  uniqueField = 'id' as keyof T,
  optionField,
  getOptionLabel,
  defaultValue,
  label,
  errorMessage,
  displayError = false,
  loading = false,
  disabled = false,
  multiple,
  onChange,
  onBlur,
  value,
  onFocus,
  renderOption,
  style,
  filterOptions,
  className
}: AutocompleteProps<T, Multiple, DisableClearable, FreeSolo>) {
  const defaultInternalValue = (multiple ? [] : null) as AutocompleteValue<
    T,
    Multiple,
    DisableClearable,
    FreeSolo
  >;

  const [internalValue, setInternalValue] = useState(
    value ?? defaultInternalValue
  );

  useEffect(() => {
    setInternalValue(value ?? defaultInternalValue);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const handleOnChange = (
    _event: React.SyntheticEvent<Element, Event>,
    value: AutocompleteValue<T, Multiple, DisableClearable, FreeSolo>
  ) => {
    onChange(value);
    setInternalValue(value);
  };

  return (
    <MuiAutocomplete
      id={id}
      multiple={multiple}
      options={options}
      loading={loading}
      value={internalValue}
      defaultValue={defaultValue}
      disabled={disabled}
      onBlur={onBlur}
      filterOptions={filterOptions}
      onFocus={onFocus}
      onChange={handleOnChange}
      getOptionLabel={
        getOptionLabel ??
        (option => String(optionField ? option[optionField as string] : option))
      }
      isOptionEqualToValue={
        !internalValue
          ? undefined
          : (option, value) => option[uniqueField] === value[uniqueField]
      }
      renderOption={(props, option, state, ownerState) =>
        renderOption ? (
          renderOption(props, option, state, ownerState)
        ) : (
          <li {...props}>{optionField ? option[optionField] : option}</li>
        )
      }
      renderInput={params => (
        <TextField
          {...params}
          inputProps={{
            ...params.inputProps,
            autoComplete: 'new-password'
          }}
          label={label}
          error={displayError && errorMessage !== undefined}
          helperText={displayError && errorMessage}
          variant="outlined"
          style={{ width: '100%' }}
        />
      )}
      className={className ?? ''}
      style={style ?? { marginBottom: '1.5rem' }}
    />
  );
}
